import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { ArrowLeft, Check, Copy, Mail, MessageSquare, Phone, X } from "lucide-react";

/*
 * Student profile for the guided demo: standing in the class, parents and
 * guardians with their contact details, and a message to a parent. Sample
 * people only. Nothing is saved or sent.
 */

type Guardian = {
  name: string;
  short: string;
  relation: string;
  primary?: boolean;
  email: string;
  phone: string;
  prefers: string;
};

type WorkTone = "ok" | "low" | "missing" | "waiting";

export type DemoProfile = {
  fullName: string;
  firstName: string;
  initials: string;
  tone: string;
  gradeLevel: string;
  percent: number;
  letter: string;
  missing: number;
  lastActive: string;
  guardians: Guardian[];
  work: { title: string; status: string; tone: WorkTone }[];
};

export const DEMO_PROFILES: Record<string, DemoProfile> = {
  maya: {
    fullName: "Maya Robinson",
    firstName: "Maya",
    initials: "MR",
    tone: "teal",
    gradeLevel: "Grade 9",
    percent: 94,
    letter: "A",
    missing: 0,
    lastActive: "Active today",
    guardians: [
      { name: "Denise Robinson", short: "Denise", relation: "Mother", primary: true, email: "denise.robinson@example.com", phone: "(951) 555-0118", prefers: "Prefers email" },
    ],
    work: [
      { title: "Check Your Understanding 1.1", status: "35 / 40", tone: "ok" },
      { title: "Homework 1.1", status: "10 / 10", tone: "ok" },
      { title: "Practice 1.1", status: "20 / 20", tone: "ok" },
    ],
  },
  jordan: {
    fullName: "Jordan Torres",
    firstName: "Jordan",
    initials: "JT",
    tone: "amber",
    gradeLevel: "Grade 9",
    percent: 82,
    letter: "B-",
    missing: 1,
    lastActive: "Active yesterday",
    guardians: [
      { name: "Luis Torres", short: "Luis", relation: "Father", primary: true, email: "luis.torres@example.com", phone: "(951) 555-0167", prefers: "Prefers text" },
    ],
    work: [
      { title: "Check Your Understanding 1.1", status: "25 / 40", tone: "low" },
      { title: "Homework 1.1", status: "Missing", tone: "missing" },
      { title: "Practice 1.1", status: "17 / 20", tone: "ok" },
    ],
  },
  ava: {
    fullName: "Ava Lopez",
    firstName: "Ava",
    initials: "AL",
    tone: "violet",
    gradeLevel: "Grade 9",
    percent: 61,
    letter: "D-",
    missing: 3,
    lastActive: "Last active 4 days ago",
    guardians: [
      { name: "Karen Lopez", short: "Karen", relation: "Mother", primary: true, email: "karen.lopez@example.com", phone: "(951) 555-0142", prefers: "Prefers email · English" },
      { name: "Mark Lopez", short: "Mark", relation: "Father", email: "mark.lopez@example.com", phone: "(951) 555-0193", prefers: "Prefers text · Spanish" },
    ],
    work: [
      { title: "Check Your Understanding 1.1", status: "25 / 40", tone: "low" },
      { title: "Homework 1.1", status: "Missing", tone: "missing" },
      { title: "Practice 1.1", status: "Missing", tone: "missing" },
      { title: "Unit 1 Readiness Check", status: "Missing", tone: "missing" },
    ],
  },
  diego: {
    fullName: "Diego Martinez",
    firstName: "Diego",
    initials: "DM",
    tone: "green",
    gradeLevel: "Grade 9",
    percent: 88,
    letter: "B+",
    missing: 0,
    lastActive: "Active today",
    guardians: [
      { name: "Rosa Martinez", short: "Rosa", relation: "Grandmother", primary: true, email: "rosa.martinez@example.com", phone: "(951) 555-0125", prefers: "Prefers a phone call" },
    ],
    work: [
      { title: "Check Your Understanding 1.1", status: "40 / 40", tone: "ok" },
      { title: "Homework 1.1", status: "9 / 10", tone: "ok" },
      { title: "Practice 1.1", status: "18 / 20", tone: "ok" },
    ],
  },
};

type Starter = "missing" | "talk" | "good";

const STARTERS: { id: Starter; label: string }[] = [
  { id: "missing", label: "Missing work" },
  { id: "talk", label: "Ask to talk" },
  { id: "good", label: "Good news" },
];

function starterText(kind: Starter, student: DemoProfile, guardian: Guardian, course: string) {
  const hi = `Hi ${guardian.short},\n\n`;
  const end = `\n\nThank you,\nYour ${course} teacher`;
  if (kind === "missing") {
    const count = student.missing === 1 ? "1 missing assignment" : `${student.missing} missing assignments`;
    return `${hi}I wanted to let you know ${student.firstName} has ${count} in ${course}. The list is attached. Could you check in with ${student.firstName} this week? I'm happy to help in class too.${end}`;
  }
  if (kind === "talk") {
    return `${hi}I'd like to set up a quick call about how ${student.firstName} is doing in ${course}. What times work for you this week?${end}`;
  }
  return `${hi}I wanted to share some good news: ${student.firstName} has been doing great work in ${course} lately.${end}`;
}

export default function DemoStudentProfile({
  student,
  course,
  onClose,
  onMessageStudent,
}: {
  student: DemoProfile;
  course: string;
  onClose: () => void;
  onMessageStudent: () => void;
}) {
  const [writingTo, setWritingTo] = useState<Guardian | null>(null);
  const [starter, setStarter] = useState<Starter>("missing");
  const [draft, setDraft] = useState("");
  const [attach, setAttach] = useState(true);
  const [sent, setSent] = useState(false);
  const [copied, setCopied] = useState("");
  const needsHelp = student.percent < 70 || student.missing >= 2;
  const missingWork = student.work.filter((item) => item.tone === "missing");

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const startMessage = (guardian: Guardian) => {
    const kind: Starter = student.missing > 0 ? "missing" : "good";
    setWritingTo(guardian);
    setStarter(kind);
    setDraft(starterText(kind, student, guardian, course));
    setAttach(missingWork.length > 0);
    setSent(false);
  };

  const pickStarter = (kind: Starter) => {
    if (!writingTo) return;
    setStarter(kind);
    setDraft(starterText(kind, student, writingTo, course));
    setSent(false);
  };

  const copyPhone = (guardian: Guardian) => {
    void navigator.clipboard?.writeText(guardian.phone).catch(() => undefined);
    setCopied(guardian.name);
    window.setTimeout(() => setCopied((name) => (name === guardian.name ? "" : name)), 1600);
  };

  return createPortal(
    <div className="al-demo-prof-backdrop" onClick={onClose} role="presentation">
      <aside
        className="al-demo-prof"
        role="dialog"
        aria-modal="true"
        aria-labelledby="al-demo-prof-name"
        onClick={(event) => event.stopPropagation()}
      >
        <header className="al-demo-prof-head">
          <span className={`al-demo-ava al-demo-ava--${student.tone} al-demo-prof-ava`} aria-hidden="true">
            {student.initials}
          </span>
          <div className="al-demo-prof-who">
            <p id="al-demo-prof-name" className="al-demo-prof-name">
              {student.fullName}
            </p>
            <p className="al-demo-prof-sub">
              {student.gradeLevel} · {course} · Sample student
            </p>
          </div>
          <button type="button" className="al-demo-dm-close" onClick={onClose} aria-label="Close profile">
            <X size={16} aria-hidden="true" />
          </button>
        </header>

        <div className="al-demo-prof-body">
          {writingTo ? (
            <section className="al-demo-prof-compose" aria-label={`Message ${writingTo.name}`}>
              <button type="button" className="al-demo-btn al-demo-btn--ghost al-demo-prof-back" onClick={() => setWritingTo(null)}>
                <ArrowLeft size={15} aria-hidden="true" />
                Back to profile
              </button>
              <h3 className="al-demo-prof-h">Message {writingTo.name}</h3>
              <p className="al-demo-prof-note">
                {writingTo.relation} of {student.firstName}. Goes to their AcuityLearn inbox with a copy to {writingTo.email}.
              </p>
              <p className="al-demo-prof-label">Start from</p>
              <div className="al-demo-prof-starters" role="group" aria-label="Start from">
                {STARTERS.map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    className={`al-demo-prof-starter${starter === item.id ? " is-on" : ""}`}
                    aria-pressed={starter === item.id}
                    onClick={() => pickStarter(item.id)}
                  >
                    {item.label}
                  </button>
                ))}
              </div>
              <label className="al-demo-sr" htmlFor="al-demo-prof-text">
                Message
              </label>
              <textarea
                id="al-demo-prof-text"
                className="al-demo-prof-text"
                value={draft}
                rows={9}
                onChange={(event) => {
                  setDraft(event.target.value);
                  setSent(false);
                }}
              />
              {missingWork.length ? (
                <label className="al-demo-prof-attach">
                  <input type="checkbox" checked={attach} onChange={(event) => setAttach(event.target.checked)} />
                  Attach {student.firstName}'s missing work list ({missingWork.length})
                </label>
              ) : null}
              <div className="al-demo-prof-send">
                {sent ? (
                  <span className="al-demo-prof-sent" role="status">
                    <Check size={15} aria-hidden="true" /> Sent. Demo only, nothing was delivered.
                  </span>
                ) : null}
                <button type="button" className="al-demo-btn" onClick={() => setSent(true)} disabled={!draft.trim()}>
                  Send message
                </button>
              </div>
            </section>
          ) : (
            <>
              <section className={`al-demo-prof-standing${needsHelp ? " is-alert" : ""}`} aria-label="Standing">
                <div className="al-demo-prof-score">
                  <strong>{student.percent}%</strong>
                  <span>{student.letter}</span>
                  {needsHelp ? <span className="al-demo-prof-flag">Needs attention</span> : null}
                </div>
                <p className="al-demo-prof-facts">
                  <span>{course} so far</span>
                  <span>{student.missing ? `${student.missing} missing` : "Nothing missing"}</span>
                  <span>{student.lastActive}</span>
                </p>
              </section>

              <section aria-labelledby="al-demo-prof-guardians">
                <h3 id="al-demo-prof-guardians" className="al-demo-prof-cap">
                  Parents and guardians
                </h3>
                <ul className="al-demo-prof-guardian-list">
                  {student.guardians.map((guardian) => (
                    <li key={guardian.name} className="al-demo-prof-guardian">
                      <p className="al-demo-prof-gname">
                        <strong>{guardian.name}</strong>
                        <span>{guardian.relation}</span>
                        {guardian.primary ? <span className="al-demo-prof-primary">Primary contact</span> : null}
                      </p>
                      <p className="al-demo-prof-contact">
                        <Mail size={14} aria-hidden="true" /> {guardian.email}
                      </p>
                      <p className="al-demo-prof-contact">
                        <Phone size={14} aria-hidden="true" /> {guardian.phone}
                      </p>
                      <p className="al-demo-prof-prefers">{guardian.prefers}</p>
                      <div className="al-demo-prof-actions">
                        <button type="button" className="al-demo-btn" onClick={() => startMessage(guardian)}>
                          <MessageSquare size={15} aria-hidden="true" />
                          Message {guardian.short}
                        </button>
                        <button type="button" className="al-demo-btn al-demo-btn--ghost" onClick={() => copyPhone(guardian)}>
                          {copied === guardian.name ? <Check size={15} aria-hidden="true" /> : <Copy size={15} aria-hidden="true" />}
                          {copied === guardian.name ? "Copied" : "Copy phone"}
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              </section>

              <section aria-labelledby="al-demo-prof-work">
                <h3 id="al-demo-prof-work" className="al-demo-prof-cap">
                  Recent work
                </h3>
                <ul className="al-demo-prof-work">
                  {student.work.map((item) => (
                    <li key={item.title}>
                      <span>{item.title}</span>
                      <span className={`al-demo-prof-status al-demo-prof-status--${item.tone}`}>{item.status}</span>
                    </li>
                  ))}
                </ul>
              </section>

              <button type="button" className="al-demo-btn al-demo-btn--ghost al-demo-prof-dm" onClick={onMessageStudent}>
                <MessageSquare size={15} aria-hidden="true" />
                Message {student.firstName}
              </button>
            </>
          )}
        </div>
      </aside>
    </div>,
    document.body,
  );
}
